<template>
  <div class="client-candidate-results">
    <section class="page-header">
      <div class="container">
        <router-link :to="backTo" class="back-link">
          <i class="fas fa-arrow-left me-1"></i> Retour
        </router-link>
        <div v-if="application" class="mt-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div class="d-flex align-items-center gap-3">
            <div class="candidate-avatar-lg">
              {{ initials }}
            </div>
            <div>
              <h1 class="page-title mb-1">
                {{ application.candidate.first_name }} {{ application.candidate.last_name }}
              </h1>
              <div class="page-subtitle">
                <i class="fas fa-briefcase me-1"></i>
                {{ application.job_offer.title }}
              </div>
              <div class="page-subtitle mt-1">
                <span class="me-3"><i class="fas fa-envelope me-1"></i> {{ application.candidate.email }}</span>
                <span v-if="application.candidate.phone">
                  <i class="fas fa-phone me-1"></i> {{ application.candidate.phone }}
                </span>
              </div>
            </div>
          </div>
          <div class="header-actions">
            <button
              class="btn btn-light"
              @click="downloadPdf"
              :disabled="downloadingPdf"
            >
              <span v-if="downloadingPdf">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Génération...
              </span>
              <span v-else>
                <i class="fas fa-file-pdf me-1"></i> Télécharger le PDF
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <div class="container py-5">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 text-muted">Chargement des résultats...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="alert alert-warning">{{ error }}</div>

      <!-- Results -->
      <div v-else>
        <div v-if="!testResults.length" class="empty-state">
          <i class="fas fa-clipboard-list"></i>
          <h5>Aucun test complété</h5>
          <p class="text-muted mb-0">
            Le candidat n'a pas encore complété ses tests psychométriques.
          </p>
        </div>

        <div v-else>
          <!-- Tabs nav -->
          <div class="tabs-nav mb-4">
            <button
              v-for="(result, idx) in testResults"
              :key="result.test_type_id"
              class="tab-btn"
              :class="{ active: activeTab === idx }"
              @click="activeTab = idx"
            >
              <i class="me-1" :class="getTestIcon(result.test_type)"></i>
              {{ result.test_name }}
            </button>
          </div>

          <!-- Active test result -->
          <div v-if="currentResult" :key="currentResult.test_type_id" class="test-detail-card">
            <div class="test-header">
              <div class="test-icon-box" :class="'tint-' + currentResult.test_type">
                <i :class="getTestIcon(currentResult.test_type)"></i>
              </div>
              <div>
                <h3 class="test-title">{{ currentResult.test_name }}</h3>
                <div class="test-date" v-if="currentResult.completed_at">
                  <i class="far fa-clock me-1"></i>
                  Complété le {{ formatDate(currentResult.completed_at) }}
                </div>
              </div>
            </div>

            <!-- Chart -->
            <div class="row g-4 mt-2">
              <div class="col-lg-6">
                <div class="chart-section">
                  <h5 class="subsection-title">
                    <i class="fas fa-chart-radar me-2"></i> Visualisation des scores
                  </h5>
                  <img
                    v-if="getChartUrl(currentResult)"
                    :src="getChartUrl(currentResult)"
                    :alt="currentResult.test_name"
                    class="chart-image"
                  />
                  <div v-else class="chart-placeholder">
                    <i class="fas fa-chart-bar"></i>
                    <p class="text-muted mt-2">Visualisation indisponible</p>
                  </div>
                </div>
              </div>

              <!-- Scores detail -->
              <div class="col-lg-6">
                <div class="scores-section">
                  <h5 class="subsection-title">
                    <i class="fas fa-list-ol me-2"></i> Détail des scores
                  </h5>
                  <div v-if="scoresList.length" class="scores-list">
                    <div v-for="item in scoresList" :key="item.key" class="score-item">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="score-label">{{ item.key }}</span>
                        <span class="score-value">{{ formatScore(item.value) }}</span>
                      </div>
                      <div class="score-bar">
                        <div
                          class="score-bar-fill"
                          :style="{ width: scorePct(item.value) + '%' }"
                          :class="getScoreClass(item.value)"
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-muted fst-italic">
                    Aucun score détaillé disponible.
                  </div>
                </div>
              </div>
            </div>

            <!-- Interpretation -->
            <div class="interpretation-section mt-4" v-if="currentResult.interpretation">
              <h5 class="subsection-title">
                <i class="fas fa-brain me-2"></i>
                Interprétation professionnelle
              </h5>
              <div class="interpretation-box">
                <div v-if="currentResult.interpretation.summary" class="mb-3">
                  <h6 class="mb-2">Synthèse</h6>
                  <p class="interpretation-text">{{ currentResult.interpretation.summary }}</p>
                </div>

                <div v-if="interpretationSections.strengths.length" class="mb-3">
                  <h6 class="mb-2 text-success">
                    <i class="fas fa-check-circle me-1"></i> Points forts
                  </h6>
                  <ul class="interpretation-list">
                    <li v-for="(s, i) in interpretationSections.strengths" :key="'s' + i">{{ s }}</li>
                  </ul>
                </div>

                <div v-if="interpretationSections.improvements.length" class="mb-3">
                  <h6 class="mb-2 text-warning">
                    <i class="fas fa-arrow-trend-up me-1"></i> Axes de développement
                  </h6>
                  <ul class="interpretation-list">
                    <li v-for="(s, i) in interpretationSections.improvements" :key="'i' + i">{{ s }}</li>
                  </ul>
                </div>

                <div v-if="interpretationSections.recommendations.length">
                  <h6 class="mb-2 text-primary">
                    <i class="fas fa-lightbulb me-1"></i> Recommandations
                  </h6>
                  <ul class="interpretation-list">
                    <li v-for="(s, i) in interpretationSections.recommendations" :key="'r' + i">{{ s }}</li>
                  </ul>
                </div>
              </div>
            </div>
            <div v-else class="alert alert-info mt-4">
              <i class="fas fa-info-circle me-2"></i>
              L'interprétation détaillée est en cours de préparation. Téléchargez le PDF pour obtenir l'analyse complète.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import clientApi from '@/services/clientApi'

export default {
  name: 'ClientCandidateResults',
  inject: ['ToastSuccess', 'ToastError'],
  data() {
    return {
      loading: true,
      error: null,
      application: null,
      testResults: [],
      activeTab: 0,
      downloadingPdf: false,
    }
  },
  computed: {
    backTo() {
      if (this.application && this.application.job_offer) {
        return { name: 'ClientOfferDetail', params: { id: this.application.job_offer.id } }
      }
      return { name: 'ClientOffers' }
    },
    initials() {
      if (!this.application) return ''
      const f = this.application.candidate.first_name || ''
      const l = this.application.candidate.last_name || ''
      return ((f.charAt(0) || '') + (l.charAt(0) || '')).toUpperCase()
    },
    currentResult() {
      return this.testResults[this.activeTab] || null
    },
    scoresList() {
      if (!this.currentResult || !this.currentResult.scores) return []
      return Object.entries(this.currentResult.scores).map(([key, value]) => ({
        key,
        value: typeof value === 'object' && value !== null ? (value.score || value.value || 0) : value,
      }))
    },
    interpretationSections() {
      const empty = { strengths: [], improvements: [], recommendations: [] }
      const interp = this.currentResult && this.currentResult.interpretation
      if (!interp) return empty
      return {
        strengths: this.toArray(interp.strengths),
        improvements: this.toArray(interp.improvements || interp.development_areas),
        recommendations: this.toArray(interp.recommendations),
      }
    },
  },
  mounted() {
    this.loadResults()
  },
  watch: {
    '$route.params.applicationId'() {
      this.loadResults()
    },
  },
  methods: {
    toArray(val) {
      if (!val) return []
      if (Array.isArray(val)) return val
      if (typeof val === 'string') {
        return val.split('\n').map((s) => s.trim()).filter(Boolean)
      }
      return []
    },
    async loadResults() {
      this.loading = true
      this.error = null
      const id = this.$route.params.applicationId
      try {
        const { data } = await clientApi.get(`/client/applications/${id}`)
        const payload = data.data || data
        this.application = payload.application || null
        this.testResults = payload.test_results || []
        this.activeTab = 0
      } catch (e) {
        this.error = 'Impossible de charger les résultats de ce candidat.'
      } finally {
        this.loading = false
      }
    },
    getTestIcon(type) {
      const icons = {
        personality: 'fas fa-user-circle',
        behavioral: 'fas fa-users',
        managerial: 'fas fa-chess-king',
        commercial: 'fas fa-handshake',
        motivation: 'fas fa-fire',
      }
      return icons[type] || 'fas fa-clipboard-list'
    },
    getChartUrl(result) {
      if (!result || !result.chart_data) return null
      try {
        // Visualization services return chart_data in different shapes:
        // - Personality       : { labels, datasets }
        // - Behavioral/Managerial/Motivation : { type, data: {labels, datasets}, options }
        // - Commercial        : { primary_chart: {type, data, options}, secondary_chart: {...} }
        let source = result.chart_data
        if (typeof source === 'string') {
          try { source = JSON.parse(source) } catch (e) { return null }
        }
        if (source.primary_chart) source = source.primary_chart

        let chartData
        let chartType
        let chartOptions
        if (source.data && (source.data.labels || source.data.datasets)) {
          chartData = source.data
          chartType = source.type
          chartOptions = source.options
        } else {
          chartData = source
        }

        let config = result.chart_configuration || {}
        if (typeof config === 'string') {
          try { config = JSON.parse(config) } catch (e) { config = {} }
        }
        const finalConfig = {
          type: chartType || config.type || 'bar',
          data: chartData,
          options: this.buildSafeOptions(chartOptions || config.options || {}),
        }
        const encoded = encodeURIComponent(JSON.stringify(finalConfig))
        return `https://quickchart.io/chart?c=${encoded}&width=500&height=400&bkg=white`
      } catch (e) {
        return null
      }
    },
    buildSafeOptions(options) {
      const stripFns = (value) => {
        if (Array.isArray(value)) return value.map(stripFns)
        if (value && typeof value === 'object') {
          const out = {}
          for (const k of Object.keys(value)) {
            const v = value[k]
            if (typeof v === 'function') continue
            if (typeof v === 'string' && v.trim().startsWith('function')) continue
            out[k] = stripFns(v)
          }
          return out
        }
        return value
      }
      const safe = { responsive: true, maintainAspectRatio: true }
      if (options.scales) safe.scales = stripFns(options.scales)
      if (options.plugins) safe.plugins = stripFns(options.plugins)
      return safe
    },
    formatDate(iso) {
      if (!iso) return ''
      try {
        return new Date(iso).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        })
      } catch (e) {
        return ''
      }
    },
    formatScore(val) {
      if (val === null || val === undefined) return '—'
      if (typeof val === 'number') return val.toFixed(1)
      return val
    },
    scorePct(val) {
      const n = typeof val === 'number' ? val : parseFloat(val)
      if (isNaN(n)) return 0
      // Scores usually 0-10 or 0-100
      if (n <= 10) return Math.min(100, Math.max(0, n * 10))
      return Math.min(100, Math.max(0, n))
    },
    getScoreClass(val) {
      const pct = this.scorePct(val)
      if (pct >= 70) return 'score-high'
      if (pct >= 40) return 'score-medium'
      return 'score-low'
    },
    async downloadPdf() {
      this.downloadingPdf = true
      const id = this.$route.params.applicationId
      try {
        // Pré-générer les interprétations GPT manquantes
        try {
          await clientApi.post(`/client/applications/${id}/prepare-interpretations`)
        } catch (e) {
          // Continuer même si ça échoue partiellement
        }

        const response = await clientApi.get(`/client/applications/${id}/report`, {
          responseType: 'blob',
        })
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `rapport-${this.application.candidate.last_name}-${this.application.candidate.first_name}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.ToastSuccess.fire({ title: 'Rapport téléchargé avec succès' })
      } catch (e) {
        this.ToastError.fire({ title: 'Échec du téléchargement du rapport' })
      } finally {
        this.downloadingPdf = false
      }
    },
  },
}
</script>

<style scoped>
.client-candidate-results {
  min-height: 100vh;
  background: #f8f9fa;
}

.page-header {
  background: linear-gradient(135deg, #2596be 0%, #29b3cd 100%);
  color: #fff;
  padding: 2.5rem 0 3rem;
}

.back-link {
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 0.9rem;
}

.back-link:hover {
  color: #fff;
}

.candidate-avatar-lg {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #fff;
  color: #2596be;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.8rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.page-title {
  font-size: 1.9rem;
  font-weight: 700;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 0.9rem;
}

.header-actions .btn {
  font-weight: 600;
  border-radius: 10px;
  padding: 0.7rem 1.2rem;
  color: #2596be;
}

.tabs-nav {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
}

.tab-btn {
  background: #fff;
  border: 2px solid transparent;
  color: #6c757d;
  padding: 0.6rem 1.1rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s;
  cursor: pointer;
}

.tab-btn:hover {
  background: rgba(37, 150, 190, 0.08);
  color: #2596be;
}

.tab-btn.active {
  background: linear-gradient(135deg, #2596be, #29b3cd);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px rgba(37, 150, 190, 0.3);
}

.test-detail-card {
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
}

.test-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid #f0f2f5;
}

.test-icon-box {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: #fff;
  flex-shrink: 0;
}

.tint-personality {
  background: linear-gradient(135deg, #2596be, #29b3cd);
}

.tint-behavioral {
  background: linear-gradient(135deg, #e95d16, #bc6a3c);
}

.tint-managerial {
  background: linear-gradient(135deg, #6f42c1, #b76ad4);
}

.tint-commercial {
  background: linear-gradient(135deg, #28a745, #20a179);
}

.tint-motivation {
  background: linear-gradient(135deg, #fd7e14, #ffb84d);
}

.test-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.2rem;
}

.test-date {
  color: #6c757d;
  font-size: 0.85rem;
}

.subsection-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2596be;
  margin-bottom: 1rem;
}

.chart-image {
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 8px;
  display: block;
  margin: 0 auto;
}

.chart-placeholder {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 3rem;
  text-align: center;
  color: #dee2e6;
  font-size: 3rem;
}

.scores-list {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.score-item {
  padding: 0.3rem 0;
}

.score-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
  text-transform: capitalize;
}

.score-value {
  font-weight: 700;
  color: #2596be;
  font-size: 0.9rem;
}

.score-bar {
  height: 8px;
  background: #e9ecef;
  border-radius: 50px;
  overflow: hidden;
}

.score-bar-fill {
  height: 100%;
  transition: width 0.3s;
  border-radius: 50px;
}

.score-bar-fill.score-high {
  background: linear-gradient(90deg, #28a745, #20c997);
}

.score-bar-fill.score-medium {
  background: linear-gradient(90deg, #ffc107, #fd7e14);
}

.score-bar-fill.score-low {
  background: linear-gradient(90deg, #e95d16, #bc6a3c);
}

.interpretation-section {
  padding-top: 1.5rem;
  border-top: 1px solid #f0f2f5;
}

.interpretation-box {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid #2596be;
}

.interpretation-text {
  color: #2c3e50;
  line-height: 1.7;
  font-size: 0.95rem;
}

.interpretation-list {
  padding-left: 1.3rem;
  color: #2c3e50;
}

.interpretation-list li {
  margin-bottom: 0.4rem;
  line-height: 1.6;
}

.empty-state {
  background: #fff;
  border-radius: 14px;
  padding: 4rem 1.5rem;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  color: #dee2e6;
  margin-bottom: 1rem;
}
</style>
